import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const sizeConfig = {
  xs: { maxWidth: "xs" },
  sm: { maxWidth: "sm" },
  md: { maxWidth: "md" },
  lg: { maxWidth: "lg" },
  xl: { maxWidth: "xl" },
  fullScreen: {
    maxWidth: false,
    PaperProps: {
      sx: {
        width: "calc(100vw - 32px)",
        maxWidth: "none",
        maxHeight: "calc(100vh - 32px)",
        m: 2,
        display: "flex",
        flexDirection: "column",
      },
    },
  },
};

/**
 * AppDialog - Khung Dialog chuẩn dùng chung cho toàn hệ thống
 *
 * @param {boolean} open - Trạng thái hiển thị (Bắt buộc)
 * @param {string|React.ReactNode} title - Tiêu đề Dialog (BẮT BUỘC - Không tự động sinh)
 * @param {React.ReactNode} [titleIcon] - Icon bên cạnh tiêu đề
 * @param {string|React.ReactNode} [subtitle] - Tiêu đề phụ / mô tả ngắn bên dưới title
 * @param {function} [onClose] - Hàm đóng Dialog
 * @param {React.ReactNode} [children] - Nội dung bên trong DialogContent
 * @param {React.ReactNode} [actions] - Các nút thao tác trong DialogActions
 * @param {string} [size="sm"] - Kích thước tiện lợi: "xs" | "sm" | "md" | "lg" | "xl" | "fullScreen"
 * @param {string|false} [maxWidth] - Ghi đè maxWidth mặc định của MUI nếu không dùng size
 * @param {boolean} [fullWidth=true] - Tự động giãn 100% width theo maxWidth
 * @param {boolean} [loading=false] - Hiển thị thanh tiến trình và khóa hành động đóng
 * @param {boolean} [dividers=true] - Hiển thị đường kẻ phân cách trên/dưới content
 * @param {boolean} [showCloseButton=true] - Hiển thị nút 'X' đóng nhanh ở góc phải
 * @param {object} [PaperProps] - Tùy biến PaperProps của MUI Dialog
 * @param {object} [contentSx] - Tùy biến style sx cho DialogContent
 * @param {object} [headerSx] - Tùy biến style sx cho DialogTitle
 * @param {object} [actionsSx] - Tùy biến style sx cho DialogActions
 */
export default function AppDialog({
  open = false,
  title,
  titleIcon,
  subtitle,
  onClose,
  children,
  actions,
  size = "sm",
  maxWidth,
  fullWidth = true,
  loading = false,
  dividers = true,
  showCloseButton = true,
  disableEscapeKeyDown = false,
  PaperProps,
  contentSx,
  headerSx,
  actionsSx,
  ...props
}) {
  if (!title && process.env.NODE_ENV !== "production") {
    console.warn("[AppDialog] 'title' là prop bắt buộc. Vui lòng truyền tiêu đề rõ ràng cho Dialog.");
  }

  const resolvedConfig = sizeConfig[size] || sizeConfig.sm;
  const resolvedMaxWidth = maxWidth !== undefined ? maxWidth : resolvedConfig.maxWidth;

  const mergedPaperProps = {
    ...(resolvedConfig.PaperProps || {}),
    ...(PaperProps || {}),
    sx: {
      borderRadius: "12px",
      overflow: "hidden",
      ...(resolvedConfig.PaperProps?.sx || {}),
      ...(PaperProps?.sx || {}),
    },
  };

  const handleClose = (event, reason) => {
    // Khi đang loading, chặn vô tình đóng Dialog
    if (loading && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    onClose?.(event, reason);
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      maxWidth={resolvedMaxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={loading || disableEscapeKeyDown}
      PaperProps={mergedPaperProps}
      {...props}
    >
      <DialogTitle
        sx={{
          py: 1.25,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #eef2f6",
          backgroundColor: "#ffffff",
          gap: 1.5,
          ...headerSx,
        }}
      >
        {titleIcon && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {titleIcon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1, alignSelf: "center", display: "flex", flexDirection: "column" }}>
          {typeof title === "string" ? (
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#1e293b",
                fontFamily: "Inter, sans-serif",
                lineHeight: 1.4,
                alignItems: "center",
              }}
            >
              {title}
            </Typography>
          ) : (
            title
          )}

          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                fontSize: "0.8125rem",
                fontFamily: "Inter, sans-serif",
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {showCloseButton && onClose && (
          <IconButton
            size="small"
            aria-label="close"
            onClick={onClose}
            disabled={loading}
            sx={{
              color: "#94a3b8",
              p: 0.5,
              alignSelf: "center",
              flexShrink: 0,
              mr: -0.5,
              "&:hover": {
                color: "#475569",
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      {loading && (
        <LinearProgress
          sx={{
            height: 3,
            backgroundColor: "rgba(11, 92, 171, 0.12)",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "var(--sub-primary, #0b5cab)",
            },
          }}
        />
      )}

      <DialogContent
        dividers={dividers}
        sx={{
          p: 2.5,
          backgroundColor: "#ffffff",
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            px: 2.5,
            py: 1,
            borderTop: "1px solid #eef2f6",
            backgroundColor: "#f8fafc",
            gap: 1,
            "& > :not(:first-of-type)": {
              marginLeft: 0,
            },
            ...actionsSx,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
